import { useTranslation } from 'react-i18next'
import { useGetTaxonomyTerms } from '@/application/services/taxonomy.service'
import { resolveLanguage } from '@/application/i18n/language'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/components/common/Select'

const ALL_CATEGORIES = '__all__'
const TERMS_LIMIT = 50

interface CategoryFilterSelectProps {
  /** Selected category term slug, or undefined for "all categories". */
  value: string | undefined
  onChange: (slug: string | undefined) => void
  className?: string
}

/**
 * Category filter dropdown fed by the category taxonomy. Options are the most
 * used terms labelled in the current UI language; the value is the term slug,
 * which is what the `category` query param of the investigations API expects.
 */
export function CategoryFilterSelect({ value, onChange, className }: CategoryFilterSelectProps) {
  const { t, i18n } = useTranslation()
  const { data: terms } = useGetTaxonomyTerms({
    locale: resolveLanguage(i18n.language),
    query: '',
    limit: TERMS_LIMIT,
  })

  return (
    <Select
      value={value ?? ALL_CATEGORIES}
      onValueChange={(next) => onChange(next === ALL_CATEGORIES ? undefined : next)}
    >
      <SelectTrigger className={className} aria-label={t('investigations.filter_category_label')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_CATEGORIES}>{t('investigations.filter_category')}</SelectItem>
        {(Array.isArray(terms) ? terms : []).map((term) => (
          <SelectItem key={term.id} value={term.slug}>
            {term.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
